import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Typography, Tooltip } from '@mui/material';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { SearchableChip } from 'modules/common/components/Chip/SearchableChip';
import { LabelType } from 'graphql/types';
import { useAcceptCptSuggestion } from 'modules/timesheet-labels/hooks';
import { useMessage } from 'modules/intl';
import { ChipProps } from '../schema';
import DependantLabels from '../DependantLabels';
import { TimesheetLabel } from '../../labelSchema';
import { useChipState } from '../../../common/hooks/useChipState';
import useProjects from './useProjects';
import useProjectOnSelect from './useProjectOnSelect';

const getOptionSelected = (
  option: WbsLabelValueSearchResult,
  selected: WbsLabelValueSearchResult
): boolean => option.id === selected?.id;

export const ProjectChip: React.FC<ChipProps> = ({
  dependantLabels,
  onSelect,
  disabled = false
}: ChipProps) => {
  const [projectChipOpen, setProjectChipOpen] = useState(false);

  const [labels, setLabels] = useState(dependantLabels);

  const label = useMemo(() => dependantLabels.project?.label, [
    dependantLabels.project
  ]);
  useEffect(() => {
    setLabels(dependantLabels);
  }, [dependantLabels, projectChipOpen]);

  const { options, handleSearch, loading, hasMore } = useProjects(
    labels,
    projectChipOpen
  );
  const { isEmptyValue: isEmpty } = useChipState(label);

  const openHandler = useCallback(() => {
    handleSearch('');
    setProjectChipOpen(true);
  }, [handleSearch, setProjectChipOpen]);

  const onClose = useCallback(() => {
    setProjectChipOpen(false);
    setLabels(dependantLabels);
  }, [setProjectChipOpen, dependantLabels, setLabels]);

  const onProjectSelect = useProjectOnSelect(onSelect, labels, onClose);

  const defaultRenderOption = useCallback(
    (option: WbsLabelValueSearchResult) => {
      return (
        <Tooltip
          followCursor={true}
          title={option.displayText}
          disableInteractive
        >
          <Typography variant="inherit" noWrap>
            {option.displayText}
          </Typography>
        </Tooltip>
      );
    },
    []
  );

  const getOptionLabel = useCallback(
    (option: WbsLabelValueSearchResult) => option.displayText || '',
    []
  );

  const notSelected = useMessage('timesheetLabels.notSelected');

  const _dependantLabels = useMemo(
    () =>
      Object.values(labels)
        .filter(
          (label) =>
            label?.label?.type === LabelType.Client ||
            label?.label?.type === LabelType.Program
        )
        .map((labelData) => {
          if (labelData.label?.value) {
            return labelData?.label as TimesheetLabel;
          }
          return {
            ...labelData?.label,
            ...{ value: { stringValue: notSelected } }
          };
        }),
    [labels, notSelected]
  );

  const onDelete = useCallback(
    (label: TimesheetLabel) => {
      if (labels.client && label.type === LabelType.Client) {
        const { index } = labels!.client;

        setLabels({
          ...labels,
          client: {
            index: index,
            label: {
              ...label,
              value: null
            }
          }
        });
      } else if (labels.program && label.type === LabelType.Program) {
        const { index } = labels!.program;

        setLabels({
          ...labels,
          program: {
            index: index,
            label: {
              ...label,
              value: null
            }
          }
        });
      }
    },
    [setLabels, labels]
  );

  const onChipDelete = useCallback(() => {
    onProjectSelect(null);
  }, [onProjectSelect]);

  const handleDelete = useMemo(() => {
    return !isEmpty ? { handleDelete: onChipDelete } : null;
  }, [isEmpty, onChipDelete]);

  const acceptProjectSuggestion = useAcceptCptSuggestion(
    dependantLabels,
    'project'
  );

  const handleAcceptSuggestion = useCallback(() => {
    acceptProjectSuggestion();
  }, [acceptProjectSuggestion]);

  const value = useMemo(
    () =>
      label?.value
        ? {
            id: label.value.idValue,
            displayText: label.value.stringValue ?? ''
          }
        : undefined,
    [label]
  );

  return (
    <SearchableChip
      label={label}
      onOpen={openHandler}
      handleSearch={handleSearch}
      loading={loading}
      options={options}
      onSelect={onProjectSelect}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      onClose={onClose}
      renderOption={defaultRenderOption}
      {...handleDelete}
      getOptionSelected={getOptionSelected}
      value={value}
      hasMore={hasMore}
      acceptSuggestion={handleAcceptSuggestion}
    >
      {_dependantLabels && (
        <DependantLabels
          dependantLabels={_dependantLabels}
          onDelete={onDelete}
          disabled={_dependantLabels[0]?.value?.idValue === undefined}
        />
      )}
    </SearchableChip>
  );
};

export default ProjectChip;
